import React, { useEffect, useState } from "react";
import { getAttendance } from "@/utils/pasUtils";
import { useUserInfo } from "@/hooks/useUserInfo.ts";
import { WelcomeHeader } from "@/components/WelcomeHeader";
import { Button } from "@/components/ui/button.tsx";
import { useLanguage } from "@/contexts/LanguageContext.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import {
  Calendar,
  Clock,
  AlertTriangle,
  AlertCircle,
  AlertOctagon,
  TrendingUp,
  TrendingDown,
  RefreshCw,
} from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { getSummary } from "@/utils/pasUtils.js";
import Pagination from "@/components/Pagination";
import { Skeleton } from "@/components/ui/skeleton"; // Add this import, adjust the path if needed

function Attendance(props) {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD
  
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationNumbers, setPaginationNumbers] = useState<(number | string)[]>([1, 2, 3, '...', 10]);

  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();

  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState<{
    total_hours?: number;
    completed_hours?: number;
    remaining_hours?: number;
  }>({
    total_hours: 0,
    completed_hours: 0,
    remaining_hours: 0,
  });

  // Mock data for missing hours - replace with backend data
  const [missingHoursData, setMissingHoursData] = useState({
    totalRequiredHours: 0,
    totalWorkedHours: 0,
    totalMissingHours: 0,
    daysWithMissingOuttime: 0,
    currentMonth: "March 2024",
    daysWithMissingHours: 0,
    averageMissingPerDay: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Wait for the signed-in user's email; the API errors without it.
    if (!email) return;

    async function fetchAttendance() {
      try {
        setLoading(true);
        setError("");
        const records = await getAttendance(
          VITE_USERNAMET,
          VITE_PASSWORD,
          email,
          true
        );

        setAttendance(records);
        setPaginationNumbers([1, 2, 3, '...', Math.ceil(records.length / 10)]); // Assuming 30 records per page
        console.log(records);

        const summaryResponse = await getSummary(
          VITE_USERNAMET,
          VITE_PASSWORD,
          email
        );
        setSummary(summaryResponse);

        // Calculate missing hours based on attendance data
        // setTimeout(() => {
        calculateMissingHours(records);
        // }, 2000);
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
      }
    }

    fetchAttendance();
  }, [email]);

  const handlePageChange = (page: number) => {
  setCurrentPage(page);
  
  const totalPages = Math.ceil(attendance.length / 10); // Assuming 10 records per page
  const paginationNumbers = generatePaginationNumbers(page, totalPages);
  
  setPaginationNumbers(paginationNumbers);
};

const generatePaginationNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
  if (totalPages <= 5) {
    // If total pages is 5 or less, show all pages
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const paginationNumbers: (number | string)[] = [];

  // Always show first page
  paginationNumbers.push(1);

  // Calculate the range for middle pages (1 before and 1 after current page)
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  // Add first ellipsis if there's a gap between page 1 and startPage
  if (startPage > 2) {
    paginationNumbers.push('...');
  }

  // Add middle pages (1 before and 1 after current page)
  for (let i = startPage; i <= endPage; i++) {
    paginationNumbers.push(i);
  }

  // Add second ellipsis if there's a gap between endPage and last page
  if (endPage < totalPages - 1) {
    paginationNumbers.push('...');
  }

  // Always show last page if it's different from first page
  if (totalPages > 1) {
    paginationNumbers.push(totalPages);
  }

  return paginationNumbers;
};

  // Function to calculate missing hours from attendance data
  async function calculateMissingHours(attendanceRecords) {
    if (!attendanceRecords || attendanceRecords.length === 0) return;

    const currentDate = new Date();
    const currentMonth = currentDate.toLocaleDateString("ar-SA", {
      month: "long",
      year: "numeric",
    });

    // Calculate total worked hours from attendance records
    let totalWorked = 0;
    let daysWithMissingHours = 0;
    let daysWithMissingOuttime = 0;
    let totalMissingHours = 0;
    // let averageMissingPerDay = 0;

    attendanceRecords.forEach((record) => {
      if (record.total_time) {
        // Convert total_time to hours (assuming it's in HH:MM format)
        const timeParts = record.total_time.split(":");
        const hours = parseInt(timeParts[0]) || 0;
        const minutes = parseInt(timeParts[1]) || 0;
        const totalHours = hours + minutes / 60;

        totalWorked += totalHours;

        // Check if hours are less than 8 (standard work day)
        if (record.status === "Present" && record.attendance_date.startsWith(currentDate.getFullYear().toString() + "-" + String(currentDate.getMonth() + 1).padStart(2, '0'))) {
          // if (totalHours < 8) {
          //   daysWithMissingHours++;
          //   totalMissingHours += 8 - totalHours;
          //   console.log(`Date: ${record.attendance_date}, Worked: ${totalHours}, Missing: ${8 - totalHours}`);
          // }
          if (
            record.out_time === null &&
            record.attendance_date !== new Date().toISOString().split("T")[0]
          ) {
            daysWithMissingOuttime++;
            // totalMissingHours += 8 - totalHours;
          }
          if (
            record.hours_completed == 0 &&
            record.attendance_date !== new Date().toISOString().split("T")[0]
          ) {
            daysWithMissingHours++;
            totalMissingHours += 8 - totalHours;
          }
        }
      }
    });

    const totalRequiredHours = 160; // 8 hours * 20 working days per month
    const calculatedMissingHours = Math.max(
      0,
      totalRequiredHours - totalWorked
    );

    setMissingHoursData((prev) => ({
      ...prev,
      totalWorkedHours: Number(totalWorked), // keep as number
      totalMissingHours: Number(totalMissingHours),
      currentMonth: currentMonth,
      daysWithMissingHours: daysWithMissingHours,
      daysWithMissingOuttime: daysWithMissingOuttime,
      averageMissingPerDay:
        daysWithMissingHours > 0 ? totalMissingHours / daysWithMissingHours : 0,
      loading: false,
      error: null,
    }));
  }

  const formatTime = (value: string) =>
    new Date(value).toLocaleTimeString("ar-SA-u-nu-latn", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const progress = Math.min(
    (summary.completed_hours / summary.total_hours) * 100,
    100
  );

  return (
    <div className="space-y-6 sm:space-y-8 fade-in">
      {/* Welcome Section */}
      <WelcomeHeader />

      {/* Missing Hours Summary */}
      {loading ? (
        <section className="surface overflow-hidden" aria-busy="true">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border/60 border-b border-border/60">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card p-5 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-4 rounded-full" />
                </div>
                <Skeleton className="mt-4 h-9 sm:h-10 w-20" />
                <Skeleton className="mt-4 h-6 w-16 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-8" />
            </div>
            <Skeleton className="h-2.5 w-full rounded-full" />
            <div className="flex justify-between mt-2">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        </section>
      ) : (
      <section className="surface overflow-hidden">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border/60 border-b border-border/60">
          {/* Total Required Hours */}
          <div className="bg-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {t("attendance.total_required_hours")}
              </p>
              <Clock className="h-4 w-4 shrink-0 text-primary" />
            </div>
            <p className="mt-3 text-3xl sm:text-4xl font-extrabold tabular-nums">
              {summary.total_hours}
            </p>
            <Badge variant="outline" className="mt-3">
              {t("attendance.monthly")}
            </Badge>
          </div>

          {/* Total Worked Hours */}
          <div className="bg-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {t("attendance.total_worked_hours")}
              </p>
              <TrendingUp className="h-4 w-4 shrink-0 text-success" />
            </div>
            <p className="mt-3 text-3xl sm:text-4xl font-extrabold tabular-nums text-success">
              {summary.completed_hours && summary.completed_hours.toFixed(2)}
            </p>
            <Badge
              variant="outline"
              className="mt-3 bg-success/10 text-success border-success/20"
            >
              {Math.round(
                summary.total_hours > 0 && summary.completed_hours
                  ? (summary.completed_hours / summary.total_hours) * 100
                  : 0
              )}
              % {t("attendance.completion_rate")}
            </Badge>
          </div>

          {/* Missing Hours */}
          <div className="bg-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {t("attendance.total_missing_hours")}
              </p>
              <TrendingDown className="h-4 w-4 shrink-0 text-warning" />
            </div>
            <p className="mt-3 text-3xl sm:text-4xl font-extrabold tabular-nums text-warning">
              {summary.remaining_hours && summary.remaining_hours.toFixed(2)}
            </p>
            <Badge
              variant="outline"
              className="mt-3 bg-warning/10 text-warning border-warning/20"
            >
              {Math.round(
                summary.total_hours > 0 && summary.remaining_hours
                  ? (summary.remaining_hours / summary.total_hours) * 100
                  : 0
              )}
              % {t("attendance.shortage_rate")}
            </Badge>
          </div>

          {/* Days with problems */}
          <div className="bg-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {t("attendance.days_with_missing_hours_checks")}
              </p>
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            </div>
            <p className="mt-3 text-3xl sm:text-4xl font-extrabold tabular-nums text-destructive">
              {missingHoursData.daysWithMissingOuttime +
                missingHoursData.daysWithMissingHours}
            </p>
            <Badge
              variant="outline"
              className="mt-3 bg-destructive/10 text-destructive border-destructive/20"
            >
              {t("attendance.day")}
            </Badge>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold">
              {t("attendance.monthly_progress")}
            </span>
            <span className="text-sm font-bold tabular-nums">
              {Math.round((summary.completed_hours / summary.total_hours) * 100)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-ink h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2 tabular-nums">
            <span>
              {Math.round(summary.completed_hours)} {t("attendance.hours")}
            </span>
            <span>
              {summary.total_hours} {t("attendance.hours")}
            </span>
          </div>
        </div>
      </section>
      )}

      {/* Attendance Records */}
      <section className="surface pb-2">
        <div className="surface-head">
          <h2 className="surface-title">
            <Clock className="h-5 w-5 text-primary" />
            سجل الحضور
          </h2>
        </div>

        {loading && (
          <div className="rows animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 py-5">
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/4 rounded bg-muted"></div>
                  <div className="h-3 w-1/2 rounded bg-muted"></div>
                </div>
                <div className="h-6 w-14 rounded-full bg-muted"></div>
              </div>
            ))}
          </div>
        )}
        {error && (
          <div className="text-center py-4">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        <div className="rows">
          {attendance.slice((currentPage - 1) * 10, currentPage * 10).map((record, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_auto] md:grid-cols-[10rem_1fr_auto] items-center gap-x-6 gap-y-3 py-4"
            >
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">
                  {new Date(record.attendance_date).toLocaleDateString(
                    "ar-SA",
                    { weekday: "long" }
                  )}
                </p>
                <p className="font-bold tabular-nums">
                  {new Date(record.attendance_date).toLocaleDateString(
                    "en-Us",
                    { year: "numeric", month: "2-digit", day: "2-digit" }
                  )}
                </p>
              </div>

              <div className="col-span-2 md:col-span-1 order-last md:order-none">
                {record.status === "Present" && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <p className="kv-label">{t("attendance.in_time")}</p>
                      <p className="kv-value">{formatTime(record.in_time)}</p>
                    </div>
                    <div>
                      <p className="kv-label">{t("attendance.out_time")}</p>
                      <p
                        className={`kv-value ${
                          !record.out_time ? "text-destructive/70" : ""
                        }`}
                      >
                        {record.out_time
                          ? formatTime(record.out_time)
                          : "لم يسجل الخروج"}
                      </p>
                    </div>
                    <div>
                      <p className="kv-label">{t("attendance.total_time")}</p>
                      <p className="kv-value text-primary">
                        {record.total_time
                          ? record.total_time.split(".")[0]
                          : t("common.not_calculated")}
                      </p>
                    </div>
                    <div>
                      <p className="kv-label">{t("attendance.completed_8_hours")}</p>
                      <p
                        className={`kv-value ${
                          record.hours_completed !== 1 && record.out_time
                            ? "text-destructive/70"
                            : "text-success"
                        }`}
                      >
                        {record.hours_completed == 1 && record.out_time
                          ? t("attendance.yes")
                          : t("attendance.no")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <StatusBadge
                status={
                  record.status === "Present"
                    ? "approved"
                    : record.status === "Absent"
                      ? "rejected"
                      : record.status === "On Leave"
                        ? "in_progress"
                        : "draft"
                }
              >
                {record.status === "Present" && t("status.present")}
                {record.status === "Holiday" && t("status.holiday")}
                {record.status === "On Leave" && t("status.on_leave")}
                {record.status === "Absent" && t("status.absent")}
              </StatusBadge>
            </div>
          ))}
        </div>

        {attendance.length === 0 && !loading && !error && (
          <div className="text-center py-10">
            <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t("attendance.no_records")}</p>
          </div>
        )}

        {!loading && (
        <div className="px-5 pb-3">
          <Pagination
            currentPage={currentPage}
            paginationNumbers={paginationNumbers}
            hasPreviousPage={currentPage > 1}
            hasNextPage={currentPage < Math.ceil(attendance.length / 10)}
            onPageChange={handlePageChange}
          />
        </div>
        )}
      </section>
    </div>
  );
}

export default Attendance;
